/**
 * Injury is a state, not a health bar (§17, §22).
 *
 * You get hurt and you're hurt for weeks. Body drops, options close, people
 * can see it on you. A broken hand means you can't work, which means rent,
 * which means pressure. The cost tail is the deterrent.
 *
 * Durations are placeholders: recovery curves are an open item (§21). See
 * `TUNING.injury`.
 */

import type { Rng } from './rng.js';
import { addMinutes, isBefore, MINUTES_PER_DAY } from './time.js';
import { TUNING } from './tuning.js';
import type { EventId, Injury, InjuryId, InjurySeverity, WorldState, WorldTime } from './types/index.js';

/** Where a fistfight leaves marks. Texture for the read screen and the model. */
const SITES: Readonly<Record<'minor' | 'serious', readonly string[]>> = {
  minor: ['split lip', 'black eye', 'bruised ribs', 'swollen jaw', 'skinned knuckles'],
  serious: ['broken hand', 'cracked ribs', 'broken nose', 'torn-up shoulder'],
};

export function isHealed(injury: Injury, now: WorldTime): boolean {
  return injury.duration.kind === 'temporary' && !isBefore(now, injury.duration.healsOn);
}

/** Injuries still with you. Healing is lazy (§10): nothing removes them. */
export function activeInjuries(injuries: readonly Injury[], now: WorldTime): Injury[] {
  return injuries.filter((i) => !isHealed(i, now));
}

/** Does an unhealed injury close this affordance ("work", say). */
export function closedBy(injuries: readonly Injury[], affordance: string, now: WorldTime): Injury | null {
  for (const injury of activeInjuries(injuries, now)) {
    if (injury.effects.some((e) => e.kind === 'closes' && e.affordance === affordance)) return injury;
  }
  return null;
}

/** How bad a beating was, from the blows that landed. Fists only (V0). */
export function severityFromBlows(blows: number, keptGoing: boolean): InjurySeverity | null {
  if (blows <= 0) return null;
  if (keptGoing || blows >= 3) return 'serious';
  return 'minor';
}

export function makeInjury(
  state: WorldState,
  rng: Rng,
  severity: InjurySeverity,
  cause: EventId | null,
  at: WorldTime,
): { readonly state: WorldState; readonly injury: Injury } {
  const id = `inj_${state.nextId}` as InjuryId;
  const next = { ...state, nextId: state.nextId + 1 };
  const tier = severity === 'minor' ? 'minor' : 'serious';
  const site = rng.pick(SITES[tier]);
  const injury: Injury = {
    id,
    site,
    severity,
    sustainedOn: at,
    cause,
    visible: true,
    duration: { kind: 'temporary', healsOn: addMinutes(at, days(severity) * MINUTES_PER_DAY) },
    effects: effectsFor(severity),
  };
  return { state: next, injury };
}

function days(severity: InjurySeverity): number {
  switch (severity) {
    case 'minor':
      return TUNING.injury.minor.days;
    case 'serious':
      return TUNING.injury.serious.days;
    case 'disabling':
    case 'catastrophic':
      return TUNING.injury.disabling.days;
  }
}

function effectsFor(severity: InjurySeverity): Injury['effects'] {
  switch (severity) {
    case 'minor':
      return [];
    case 'serious':
      return [
        { kind: 'stat', stat: 'body', delta: -TUNING.injury.serious.bodyDrop },
        { kind: 'closes', affordance: 'work' },
      ];
    case 'disabling':
    case 'catastrophic':
      return [
        { kind: 'stat', stat: 'body', delta: -TUNING.injury.disabling.bodyDrop },
        { kind: 'closes', affordance: 'work' },
      ];
  }
}
